import React, { useState, useEffect, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import FeedbackCollector, { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { GlobalFeedbackCollector } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector';
import { FeedbackSource } from '@confluence/global-feedback-collector/entry-points/feedbackSourceType';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getNav4GAPhase } from '@confluence/nav4-enabled/entry-points/getNav4GAPhase';
import { useUpdateNav4OptIn } from '@confluence/nav4-enabled/entry-points/useUpdateNav4OptIn';
import { useNav4OptOut } from '@confluence/nav4-enabled/entry-points/useNav4OptOut';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';

import { useNav4FeedbackCollectorStore } from './Nav4FeedbackCollectorStore';

const NAV_4_FEEDBACK_ENTRYPOINT_ID = '5002b4f2-9df8-4bb2-8798-56005ef6cbfc';
const NAV_4_FEEDBACK_GROUP_ID = 'confluence-nav-4-sidebar';

const DEFAULT_CONTEXT = [
	{
		id: 'feedback-group-id',
		value: NAV_4_FEEDBACK_GROUP_ID,
	},
];

const i18n = defineMessages({
	feedbackCollectorTitle: {
		id: 'nav4-feedback.feedback-collector.title',
		defaultMessage: 'Help us improve the new navigation',
		description: 'Title for the nav4 feedback collector modal',
	},
	feedbackCollectorTitleDetails: {
		id: 'nav4-feedback.feedback-collector.title-details',
		defaultMessage: 'We’d love to hear about your experience with the new navigation.',
		description: 'Message for the sub-title for the nav4 feedback collector modal',
	},
	feedbackCollectorTitleDetailsOptOut: {
		id: 'nav4-feedback.feedback-collector.title-details.opt-out',
		defaultMessage:
			'We’d love to hear about your experience with the new navigation. If needed, you can <a>switch back to the old navigation</a>.',
		description:
			'Message for the sub-title for the nav4 feedback collector modal. Should match page-layout.nav4.feedback-collector.title-details but include a link to opt out.',
	},
	feedbackCollectorOptOutTitle: {
		id: 'nav4-feedback.feedback-collector.opt-out.title',
		defaultMessage: 'Switch back to the old navigation',
		description: 'Title for the nav4 feedback collector modal when opting out',
	},
	feedbackCollectorOptOutTitleDetails: {
		id: 'nav4-feedback.feedback-collector.opt-out.title-details',
		defaultMessage:
			'We’re sad to see you go. To help us improve the navigation for all users, please share why the new navigation didn’t work well for you.',
		description: 'Message for the sub-title for the nav4 feedback collector modal when opting out',
	},
	customTextAreaLabel: {
		id: 'nav4-feedback.feedback-collector.custom-text-area-label',
		defaultMessage: 'Let us know why you want to switch back',
		description: 'Label for the custom text area when opting out',
	},
	submitButtonLabel: {
		id: 'nav4-feedback.feedback-collector.submit-button-label',
		defaultMessage: 'Switch back',
		description: 'Label for the submit button when opting out',
	},
});

type ModalText = 'title' | 'description' | 'textArea' | 'buttonText';

const BASIC_NAV4_FEEDBACK_COLLECTOR_TEXT: Record<ModalText, MessageDescriptor | undefined> = {
	title: i18n.feedbackCollectorTitle,
	description: i18n.feedbackCollectorTitleDetails,
	textArea: undefined,
	buttonText: undefined,
};

const OPT_OUT_NAV4_FEEDBACK_COLLECTOR_TEXT: Record<ModalText, MessageDescriptor> = {
	title: i18n.feedbackCollectorOptOutTitle,
	description: i18n.feedbackCollectorOptOutTitleDetails,
	textArea: i18n.customTextAreaLabel,
	buttonText: i18n.submitButtonLabel,
};

const getTextRecord = (optingOut: boolean) => {
	if (optingOut && !fg('confluence_nav4_ga_phase_2') && fg('confluence_nav_4')) {
		return OPT_OUT_NAV4_FEEDBACK_COLLECTOR_TEXT;
	}
	return BASIC_NAV4_FEEDBACK_COLLECTOR_TEXT;
};

export const Nav4FeedbackCollectorModal = () => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId, displayName, locale } = useSessionData();
	const [{ isOpen, optingOut }, { close, openOptOutModal }] = useNav4FeedbackCollectorStore();
	const [shouldDisplayFlag, setShouldDisplayFlag] = useState(false);

	const { updateNav4OptOut: updateAdminNav4, isAdminUpdateSuccessful } = useNav4OptOut();
	const { updateNav4OptIn: updateSiteWideNav4, isSiteWideUpdateSuccessful } = useUpdateNav4OptIn({
		newIsNav4OptedIn: false,
	});
	const isNav4DisplayedToUser = useIsNav4Enabled();
	const currentGAPhase = getNav4GAPhase();

	const textRecord = getTextRecord(optingOut);

	useEffect(() => {
		void close();
		if (isSiteWideUpdateSuccessful && isAdminUpdateSuccessful) {
			window.location.reload();
		}
	}, [close, isAdminUpdateSuccessful, isSiteWideUpdateSuccessful]);

	/**
	 * There are two scenarios where a user can opt out
	 * EAP users: on the profile menu (personal opt out)
	 * Phase 1 users: in the new features page (site-wide and personal opt out)
	 */
	const optOut = () => {
		const AUDIENCE = fg('confluence_nav_4') ? 'EAP' : currentGAPhase === 1 ? 'GA' : null;
		void updateAdminNav4(true);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'switchBackOldNavigation',
				source: 'nav4FeedbackCollector',
				attributes: {
					isTenantUpdated: !fg('confluence_nav_4'),
					isUserUpdated: isNav4DisplayedToUser,
					audience: AUDIENCE,
				},
			},
		}).fire();

		void updateSiteWideNav4();
	};

	const onSubmit = () => {
		if (optingOut) {
			optOut();
		}
	};

	// Flag is handled by GlobalFeedbackCollector component itself
	const onSubmitWithFlag = () => {
		if (optingOut) {
			optOut();
		} else {
			setShouldDisplayFlag(true);
			void close();
		}
	};

	const context = optingOut
		? [...DEFAULT_CONTEXT, { id: 'opt-out', value: 'true' }]
		: DEFAULT_CONTEXT;

	const getFeedbackTitleDetails = () => {
		//only show optItem when BETA/EAP FF is on 7 Phase two is not on
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (!optingOut && !fg('confluence_nav4_ga_phase_2') && fg('confluence_nav_4')) {
			return (
				<>
					{intl.formatMessage(i18n.feedbackCollectorTitleDetailsOptOut, {
						a: (chunks: ReactNode[]) => (
							// eslint-disable-next-line @atlassian/a11y/anchor-is-valid
							<Link href="#" onClick={openOptOutModal}>
								{chunks}
							</Link>
						),
					})}
				</>
			);
		}

		return textRecord.description && intl.formatMessage(textRecord.description);
	};

	return (
		<>
			{isOpen &&
				(fg('confluence_modernize_feedback_consolidation') ? (
					<GlobalFeedbackCollector
						showTypeField
						onClose={close}
						onCloseComplete={() => {}}
						onSubmit={onSubmit}
						feedbackSource={FeedbackSource.NAV_4}
						feedbackTitleDetails={getFeedbackTitleDetails()}
						additionalFields={{
							'feedback-group-id': NAV_4_FEEDBACK_GROUP_ID,
							...(optingOut ? { 'opt-out': 'true' } : {}),
						}}
					/>
				) : (
					<FeedbackCollector
						locale={locale}
						name={displayName ?? undefined}
						atlassianAccountId={userId ?? undefined}
						entrypointId={NAV_4_FEEDBACK_ENTRYPOINT_ID}
						onClose={close}
						onSubmit={onSubmitWithFlag}
						showTypeField
						feedbackTitle={textRecord.title && intl.formatMessage(textRecord.title)}
						feedbackTitleDetails={getFeedbackTitleDetails()}
						customTextAreaLabel={textRecord.textArea && intl.formatMessage(textRecord.textArea)}
						submitButtonLabel={textRecord.buttonText && intl.formatMessage(textRecord.buttonText)}
						additionalFields={[
							{
								id: 'customfield_10047',
								value: context.map(({ id, value }) => `${id}: ${String(value)}`).join('\n'),
							},
						]}
					/>
				))}
			<FlagGroup onDismissed={() => setShouldDisplayFlag(false)}>
				{shouldDisplayFlag && <FeedbackFlag />}
			</FlagGroup>
		</>
	);
};
